import * as React from "react";
import { ResourceDWR } from "../../../dto/ResourceDWR";
import { TextualHolding } from "../../../dto/TextualHolding";
import { HoldingsInformation } from "../../../dto/HoldingsInformation";
import { useLanguage } from "../../Language";
import { TextualHoldings } from "./TextualHoldings";
import { useLocalHoldingsBranch } from "./LocalHoldingsBranch";
import { Holdings } from "./Holdings";
import { useBranchLimits } from "../../BranchLimits";

import { Toolbar, Typography } from "@mui/material";
import { SelectedLocationsCopiesToggle } from "../../SelectedLocationsCopiesToggle";
import { LoaderFunctionArgs, redirect } from "react-router-dom";
import { getResourceByHostRecordId, getResourceById } from "./getResourceById";
import { getRedirectToDefaultTab } from "./ResourcePage";

export async function allCopiesGuard({ request, params }: LoaderFunctionArgs) {
  const titleId = params.titleId;
  const hostRecordId = params.hostRecordId;
  const data = !!titleId
    ? await getResourceById(titleId)
    : await getResourceByHostRecordId(hostRecordId);

  const basePath = !!titleId
    ? `/resource/${titleId}`
    : `/hostrecord/${hostRecordId}`;
  if (data.rtype !== 1) {
    return redirect(`${basePath}/details`);
  }

  if (data.downloadable) {
    const defaultTabPath = await getRedirectToDefaultTab(data);
    return redirect(`${basePath}/${defaultTabPath}`);
  }

  return null; // allow
}

interface BranchCopies {
  branchIdentifier: string;
  branchName: string;
  holdings: HoldingsInformation[];
  textualHoldings: TextualHolding[];
  serialHoldings: HoldingsInformation[];
}

export interface AllCopiesProps {
  resource: ResourceDWR;
}
export const AllCopies: React.FC<AllCopiesProps> = ({ resource }) => {
  const { translate } = useLanguage();
  const { branchIdentifier: localHoldingsBranchId } = useLocalHoldingsBranch();
  const { displayInCopiesBranchIds, selectedBranchIds } = useBranchLimits();

  const [viewSelected, setViewSelected] = React.useState<boolean>(true);

  // Group by branch
  // TODO update useVisibleHoldings to accept viewSelected and includeTextualHoldings then leverage here (before reduce)
  const branchCopies = [
    ...resource.holdingsInformations,
    ...resource.textualHoldings,
  ]
    .filter(({ branchIdentifier }) =>
      viewSelected && selectedBranchIds?.length
        ? selectedBranchIds.includes(branchIdentifier)
        : displayInCopiesBranchIds.includes(branchIdentifier)
    )
    .reduce((acc, holding) => {
      const { branchIdentifier, branchName } = holding;

      if (!acc[branchIdentifier]) {
        acc[branchIdentifier] = {
          branchIdentifier,
          branchName,
          holdings: [],
          textualHoldings: [],
          serialHoldings: [],
        };
      }

      if ("textualHoldingStatement" in holding) {
        acc[branchIdentifier].textualHoldings.push(holding);
      } else if (holding.serialItem) {
        acc[branchIdentifier].serialHoldings.push(holding);
      } else {
        acc[branchIdentifier].holdings.push(holding);
      }

      return acc;
    }, {} as Record<string, BranchCopies>);

  // TODO use Memo
  // Display local copies first then sort by branch name
  const sortedBranchCopies = Object.values(branchCopies).sort((a, b) =>
    a.branchIdentifier === localHoldingsBranchId
      ? -1
      : b.branchIdentifier === localHoldingsBranchId
      ? 1
      : a.branchName.localeCompare(b.branchName)
  );

  return (
    <>
      {!!selectedBranchIds?.length && (
        <Toolbar disableGutters>
          <SelectedLocationsCopiesToggle
            viewSelected={viewSelected}
            includeTextualHoldings={true}
            resource={resource}
            onChange={(viewSelected) => setViewSelected(viewSelected)}
          ></SelectedLocationsCopiesToggle>{" "}
        </Toolbar>
      )}

      {sortedBranchCopies.map((branch) => (
        <div
          style={{ marginBottom: 50, fontSize: "0.95em" }}
          key={branch.branchIdentifier}
        >
          <Typography variant="h6" component={"h4"}>
            {translate("findOnShelf")} {translate("availability_at")}{" "}
            {branch.branchName}
          </Typography>
          {branch.textualHoldings.length > 0 && (
            <TextualHoldings holdings={branch.textualHoldings} />
          )}
          {branch.holdings.length > 0 && (
            <Holdings resource={resource} holdings={branch.holdings} />
          )}
          {branch.serialHoldings.length > 0 && (
            <Holdings resource={resource} holdings={branch.serialHoldings} />
          )}
        </div>
      ))}
      {sortedBranchCopies.length === 0 && (
        <p>
          {resource.downloadable
            ? translate("downloadableResourceMessage")
            : translate("noCopies")}
        </p>
      )}
    </>
  );
};
